import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShoppingCart, Loader2, ShieldCheck, User as UserIcon } from "lucide-react";

const ROLE_TAG = { admin: "Yönetici", purchasing: "Satınalmacı", user: "Kullanıcı", warehouse: "Depo" };

/**
 * Forward a request to a specific purchase-capable user ("Satınalmacıya İlet").
 * The chosen purchaser is notified and the request is assigned to them.
 * Props: open, onOpenChange, request, onDone?
 */
export default function PurchaserSelectDialog({ open, onOpenChange, request, onDone }) {
  const [purchasers, setPurchasers] = useState([]);
  const [selected, setSelected] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected("");
    setNote("");
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/users/purchasers");
        setPurchasers(data);
      } catch (_) {
        setPurchasers([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const submit = async () => {
    if (!selected) {
      toast.error("Lütfen bir satınalmacı seçin");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/requests/${request.id}/assign-purchasing`, {
        purchaser_id: selected,
        note: note.trim(),
      });
      const name = purchasers.find((p) => p.id === selected)?.name || "satınalmacı";
      toast.success(`${name} kişisine iletildi`);
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "İletilemedi");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="purchaser-select-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5 text-amber-600" /> Satınalmacıya İlet
          </DialogTitle>
          <DialogDescription>
            Talebi hazırlaması için bir satınalmacı seçin. Seçilen kişiye bildirim gönderilir ve talep "Benim Taleplerim" listesine düşer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="max-h-64 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
            {loading ? (
              <div className="p-6 text-center text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Yükleniyor…</div>
            ) : purchasers.length === 0 ? (
              <div data-testid="no-purchasers" className="p-6 text-center text-slate-400 text-sm">
                Satın alma yetkisi olan kullanıcı yok. Yönetici, Kullanıcılar sayfasından "Satın Alma" ek yetkisi verebilir.
              </div>
            ) : (
              purchasers.map((p) => {
                const active = selected === p.id;
                const Icon = p.role === "admin" ? ShieldCheck : UserIcon;
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`purchaser-opt-${p.id}`}
                    onClick={() => setSelected(p.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${active ? "bg-indigo-50" : "hover:bg-slate-50"}`}
                  >
                    <span className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${active ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className={`block text-sm font-medium truncate ${active ? "text-indigo-900" : "text-slate-800"}`}>{p.name}</span>
                      <span className="block text-xs text-slate-400">
                        {ROLE_TAG[p.role] || p.role}
                        {(p.extra_roles || []).includes("purchasing") && p.role !== "purchasing" && p.role !== "admin" ? " · +Satın Alma" : ""}
                      </span>
                    </span>
                    <span className={`h-4 w-4 rounded-full border-2 shrink-0 ${active ? "border-indigo-600 bg-indigo-600" : "border-slate-300"}`} />
                  </button>
                );
              })
            )}
          </div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Not (opsiyonel)</Label>
            <Textarea
              data-testid="purchaser-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Satınalmacıya iletmek istediğiniz not…"
              className="mt-1.5 min-h-[64px]"
            />
          </div>

          <Button
            data-testid="purchaser-submit-btn"
            onClick={submit}
            disabled={busy || !selected}
            className="w-full h-11 gap-2 bg-amber-600 hover:bg-amber-700 text-white"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingCart className="h-4 w-4" />}
            Seçili satınalmacıya ilet
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
